import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';
import { formatPrice } from '@rcg/pricing-format';
import { Tooltip } from '@rcg/ui-react';
import useDictionary from '../../../hooks/useDictionary';
import Dictionary from '../../../utils/dictionary';
import { getBaggageIcon } from '../../../utils/icons';

const useStyles = makeStyles((Theme) => ({
  section: {},
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '6px',
  },
  title: {
    fontFamily: Theme.typography.base.bold,
    fontSize: Theme.typography.size[0],
    fontWeight: 700,
    lineHeight: '16px',
    color: '#083870',
    margin: 0,
  },
  infoIcon: {
    fontSize: '16px',
    color: Theme.palette.gray[500],
    cursor: 'pointer',
  },
  items: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    [Theme.breakpoints.up('md')]: {
      gap: '16px',
    },
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: Theme.spacing(0.5),
    fontSize: Theme.typography.size[1],
    color: Theme.palette.gray[750],
    '& svg': {
      display: 'block',
      flexShrink: 0,
      fontSize: '18px',
      color: Theme.palette.gray[500],
    },
    [Theme.breakpoints.up('md')]: {
      '& svg': {
        fontSize: '20px',
      },
    },
  },
  tooltipContent: {
    padding: '12px',
    minWidth: '220px',
  },
  tooltipTitle: {
    fontFamily: Theme.typography.base.bold,
    fontWeight: 700,
    fontSize: Theme.typography.size[1],
    color: '#083870',
    margin: '0 0 8px 0',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '4px',
    fontSize: Theme.typography.size[1],
    color: Theme.palette.gray[750],
  },
  tooltipFooter: {
    marginTop: '8px',
    fontSize: Theme.typography.size[0],
    color: Theme.palette.gray[500],
    margin: '8px 0 0 0',
  },
}));

const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const buildBaggageRows = (baggageInfo, currency, freeLabel) => {
  const { freeBaggageCount = 0, baggageCharges = [] } = baggageInfo || {};
  const rows = [];

  for (let i = 1; i <= freeBaggageCount; i += 1) {
    rows.push({ ordinal: getOrdinal(i), price: freeLabel });
  }

  (baggageCharges || []).forEach((charge, idx) => {
    const formatted = formatPrice(charge.price.value, currency || 'USD', true, true, true);
    rows.push({
      ordinal: getOrdinal(freeBaggageCount + idx + 1),
      price: `+${formatted.replace(/\s+[A-Z]{3}$/, '')}`,
    });
  });

  return rows;
};

const buildBaggageItems = (baggageInfo, currency, freeLabel, fallbackLabel) => {
  const { freeBaggageCount = 0, baggageCharges = [] } = baggageInfo || {};
  const items = [];

  for (let i = 0; i < freeBaggageCount; i += 1) {
    items.push(freeLabel);
  }

  (baggageCharges || []).forEach((charge) => {
    const formatted = formatPrice(charge.price.value, currency || 'USD', true, true, true);
    items.push(`+${formatted.replace(/\.00(\s|$)/, '$1').replace(/\s+[A-Z]{3}$/, '')}`);
  });

  if (items.length === 0) {
    items.push(fallbackLabel);
  }

  return items;
};

const BaggageSection = ({ baggageInfo, currency }) => {
  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);
  const freeLabel = getDictionary(Dictionary.airFreeLabel.key);

  const items = buildBaggageItems(
    baggageInfo,
    currency,
    freeLabel,
    getDictionary(Dictionary.airBaggageFeeApplyLabel.key),
  );

  const rows = buildBaggageRows(baggageInfo, currency, freeLabel);
  const checkedBagLabel = getDictionary(Dictionary.airCheckedBagLabel.key);

  const tooltipContent = (
    <div className={classes.tooltipContent}>
      <p className={classes.tooltipTitle}>{getDictionary(Dictionary.airBaggageTooltipTitle.key)}</p>
      {rows.length > 0 ? (
        rows.map(({ ordinal, price }) => (
          <div key={ordinal} className={classes.tooltipRow}>
            <span>
              {ordinal} {checkedBagLabel}
            </span>
            <span>{price}</span>
          </div>
        ))
      ) : (
        <div className={classes.tooltipRow}>
          {getDictionary(Dictionary.airBaggageTooltipNoBaggages.key)}
        </div>
      )}
      <p className={classes.tooltipFooter}>
        {getDictionary(Dictionary.airBaggageTooltipAditionalInfo.key)}
      </p>
    </div>
  );

  return (
    <div className={classes.section}>
      <div className={classes.titleRow}>
        <p className={classes.title}>{getDictionary(Dictionary.airFareBaggageTitle.key)}</p>
        <Tooltip element={<InfoIcon className={classes.infoIcon} />} content={tooltipContent} />
      </div>
      <div className={classes.items}>
        {items.map((label, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} className={classes.item}>
            {getBaggageIcon()}
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default BaggageSection;
